import { useState, createContext, useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showMessage } from 'app/store/fuse/messageSlice';

export const NotificationWebsocketContext = createContext(null);

export const useNotificationWebsocket = () => {
	return useContext(NotificationWebsocketContext);
};

export default function NotificationWebsocketProvider(props) {
  const dispatch = useDispatch();
  const user = useSelector(({ auth }) => auth.user);

  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [lastMessage, setLastMessage] = useState(null);

  const websocketRef = useRef(null);
  const pingTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const backoffRef = useRef(1000);
  const MAX_BACKOFF = 30000;      
  const PING_INTERVAL_MS = 28000;

  const url =
    `ws${process.env.REACT_APP_SIP_WS_SECURE === 'true' ? 's' : ''}://` +
    `${process.env.REACT_APP_SIP_WEBSOCKET}` +
    `${process.env.REACT_APP_SIP_WS_PORT?.length ? `:${process.env.REACT_APP_SIP_WS_PORT}` : ''}` +
    `${process.env.REACT_APP_NOTIFICATION_WS_PATH || ''}`;

  const startHeartbeat = () => {
    stopHeartbeat();
    pingTimerRef.current = setInterval(() => {
	const ws = websocketRef.current;
	if (ws?.readyState === WebSocket.OPEN) {
		ws.send(JSON.stringify({ "type": 'PING', "payload": { "ts": Date.now() } }));
	}
    }, PING_INTERVAL_MS);
  };

  const stopHeartbeat = () => {
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  };

  const scheduleReconnect = () => {
    if (reconnectTimerRef.current) return; 
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      openConnection();
    }, backoffRef.current);
    backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF);
  };

  const clearReconnect = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    backoffRef.current = 1000;
  };

  const openConnection = () => {
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(url);
    websocketRef.current = ws;

    ws.onopen = () => {
      setInitialized(true);
      clearReconnect();
      startHeartbeat();
    };

    ws.onmessage = (e) => {
      const { data } = e;
      if (!data) return;

      try {
        const msg = typeof data === 'string' ? JSON.parse(data) : data;
        setLastMessage(msg);

        if (msg?.type === 'PING') {
          ws.send(JSON.stringify({ type: 'PONG', ts: Date.now() })); 
          return;
        }
        if (msg?.type === 'PONG') return;

        if (msg?.message) {
          dispatch(showMessage({ message: msg.message }));
        } else if (msg?.device && msg?.status) {
          dispatch(showMessage({ message: `Device ${msg.device} is ${msg.status}` }));
        }
      } catch (err) {
        console.error('Notification WS parse error:', err);
        setError(err);
      }
    };

    ws.onerror = (event) => {
      setError(event);
    };

    ws.onclose = () => {
      setInitialized(false);
      stopHeartbeat();
      if (websocketRef.current === ws) websocketRef.current = null;

      if (user?.data?.name && user.data.name !== 'Guest') {
        scheduleReconnect();
      }
    };
  };

  const closeConnection = () => {
    setInitialized(false);
    stopHeartbeat();
    clearReconnect();

    const ws = websocketRef.current;
    if (!ws) return;

    try {
      ws.onmessage = null;
      ws.onopen = null;
      ws.onerror = null;
      ws.onclose = null;
    } catch (err) {
      console.error('Error cleaning up WebSocket handlers', err);
    }

    try {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    } catch (err) {
      console.error('Error closing WebSocket', err);
    }

    websocketRef.current = null;
  };

  useEffect(() => {
    if (user?.data?.name && user.data.name !== 'Guest') openConnection();
    else if (websocketRef.current) closeConnection();

    return () => closeConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.data?.name]);

  const value = { initialized, error, lastMessage };
  return (
    <NotificationWebsocketContext.Provider value={value}>
      {props.children}
    </NotificationWebsocketContext.Provider>
  );
}